import React, { Dispatch, SetStateAction } from 'react';

import { Center, Flex, Grid, GridItem, Heading, Text } from '@chakra-ui/react';
import Circle from 'images/highlights/circle-3.svg';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import { SearchInput } from '@/components/atoms';
import { HiddenScrollbarContainer } from '@/components/atoms/HiddenScrollbarContainer';
import { LoadingSpinner } from '@/components/atoms/LoadingSpinner';
import { Pagination } from '@/components/atoms/Pagination';
import { TextDecorationSvg } from '@/components/functional';
import { ArticleCardMd } from '@/components/molecules';
import { SectionContentWrapper } from '@/components/wrappers';
import { ArticleCardFragment } from '@/graphql/generated';
import { DECREASE_SCROLL_TOP_VALUE, HEADER_HEIGHT, LOCALES } from '@/utils/constants';

import { ArticleCategoryFilter } from '../ArticleCategoryFilter/ArticleCategoryFilter';

type AllArticlesProps = {
  isLoading: boolean;
  articles: ArticleCardFragment[];
  pageCount: number;
  selectedCategoryId: string | undefined;
  selectedPage: number;
  onSelectCategory: (id: string | undefined) => void;
  onPageChange: (page: number) => void;
  setSearchValue: Dispatch<SetStateAction<string>>;
};

export const AllArticles = ({
  isLoading,
  articles,
  pageCount,
  selectedCategoryId,
  selectedPage,
  onSelectCategory,
  onPageChange,
  setSearchValue,
}: AllArticlesProps) => {
  const { t } = useTranslation(LOCALES.ARTICLES);
  const router = useRouter();

  const handleChangePage = (page: number) => {
    onPageChange(page);
    router.push({ query: { page } });
    const headerToScrollTo = document.getElementById('articlesByCategory');
    if (headerToScrollTo) {
      const scrollTopValue =
        headerToScrollTo.getBoundingClientRect().top +
        window.scrollY -
        parseInt(HEADER_HEIGHT) -
        DECREASE_SCROLL_TOP_VALUE;
      window.scrollTo({
        top: scrollTopValue,
        behavior: 'smooth',
      });
    }
  };

  return (
    <SectionContentWrapper
      pt={20}
      px={{ base: 5, md: 20 }}
      pb={{
        base: 8,
        sm: 14,
        md: 8,
      }}
    >
      <Heading
        mb={{
          base: 6,
          sm: 8,
        }}
        color="purple.500"
        variant={{
          base: 'component-heading-large/xl',
          sm: 'component-heading-large/2xl',
          lg: 'component-heading-large/3xl',
        }}
        id="articlesByCategory"
      >
        {t('articlesBy')}{' '}
        <TextDecorationSvg variant="circle" color="lime" line={Circle} scaleX={1.65}>
          <Text as="span" position="relative" zIndex={1}>
            {t('circled')}
          </Text>
        </TextDecorationSvg>
      </Heading>
      <HiddenScrollbarContainer
        flexWrap={{
          base: 'nowrap',
          md: 'wrap',
        }}
        alignItems="center"
        overflowY="auto"
        mb={4}
        w="110%"
        pr="10%"
        gap={2}
      >
        <ArticleCategoryFilter selectedCategoryId={selectedCategoryId} onSelectCategory={onSelectCategory} />
      </HiddenScrollbarContainer>
      <Flex
        mb={{
          base: 8,
          sm: 12,
          md: 16,
        }}
      >
        <SearchInput setInputValue={setSearchValue} />
      </Flex>

      {isLoading ? (
        <Center minH="md">
          <LoadingSpinner />
        </Center>
      ) : (
        <Grid
          gridTemplateColumns={{
            base: 'repeat(auto-fill, minmax(280px, 1fr))',
            md: 'repeat(auto-fill, minmax(338px, 1fr))',
            xl: 'repeat(3, 1fr)',
          }}
          rowGap={{
            base: 8,
            sm: 12,
            md: 18,
          }}
          columnGap={6}
        >
          {articles?.map((article) => {
            return (
              article && (
                <GridItem key={article.slug}>
                  <ArticleCardMd article={article} />
                </GridItem>
              )
            );
          })}
        </Grid>
      )}

      <Flex
        w="100%"
        justifyContent="center"
        mt={{
          base: 10,
          sm: 14,
          md: 18,
        }}
      >
        {pageCount > 1 ? (
          <Pagination totalNoOfPages={pageCount} currentPage={selectedPage} onPageChange={handleChangePage} />
        ) : null}
      </Flex>
    </SectionContentWrapper>
  );
};
